/* Wrapper used by TextTooltip.tsx to host hover handlers without
   introducing any layout — children render as direct flex / inline
   children of whatever parent context the tooltip was placed in. */
.triggerWrap { display: contents; }

/* Styled-tooltip surface — single, shared design across calendar
   hover tooltips (date events, lane labels) and shared trigger
   wrappers in src/shared/inputs/TextTooltip.tsx. Visual treatment
   mirrors .voyageTooltipBody so all hover-tooltip surfaces feel like
   one design. .tooltipVisible toggles the show/hide deterministically
   so callers can manage their own delay timing. */
.chartTooltip {
  position: fixed;
  /* Above modals (100 / 110) so tooltips on dialog content still
     surface — kept below any toast/notification layer (500+). */
  z-index: 300;
  /* Grow to the natural single-line width of the content so short
     meta lines fit on one line, then cap at max-width so long
     descriptions wrap inside the box instead of overflowing it. */
  width: max-content;
  max-width: 480px;
  padding: 0.55rem 0.75rem 0.6rem;
  background: var(--primary-soft);
  border: 1px solid var(--primary);
  border-radius: var(--radius-md);
  box-shadow:
    0 0 0 1px rgba(99, 102, 241, 0.2),
    var(--shadow-lg);
  font-family: var(--font-sans);
  font-size: 0.78rem;
  line-height: 1.35;
  color: var(--text-primary);
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.08s ease-out;
}
.tooltipVisible {
  opacity: 1;
}
.eventTooltipEntry {
  display: flex;
  gap: 0.55rem;
  align-items: stretch;
}
.eventTooltipEntry + .eventTooltipEntry {
  margin-top: 0.45rem;
  padding-top: 0.45rem;
  border-top: 1px solid color-mix(in srgb, var(--primary) 35%, transparent);
}
.eventTooltipStripe {
  flex-shrink: 0;
  width: 3px;
  border-radius: 2px;
  background: var(--primary);
}
.eventTooltipTextBox {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.eventTooltipDesc {
  font-weight: 600;
  font-size: 0.82rem;
  color: var(--text-primary);
}
.eventTooltipMeta {
  margin-top: 0.15rem;
  font-family: var(--font-mono);
  font-size: 0.7rem;
  letter-spacing: 0.02em;
  color: var(--text-secondary);
}
/* Two-column property grid: muted label on the left, secondary value
   on the right. Used to align "Identity / Status" or "Token / Session"
   key-value pairs in the auth pill tooltips so labels and values line
   up vertically. */
.eventTooltipProps {
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 0.7rem;
  row-gap: 0.1rem;
  margin-top: 0.25rem;
  font-family: var(--font-mono);
  font-size: 0.7rem;
  letter-spacing: 0.02em;
}
.eventTooltipPropLabel {
  color: var(--text-muted);
  white-space: nowrap;
}
.eventTooltipPropValue {
  color: var(--text-secondary);
  white-space: nowrap;
}
/* Hint footer for interactive affordances (e.g. "Click to … · Drag to …").
   Sits outside the stripe-bearing entry so it reads as guidance, not
   data — separator rule + muted italics distinguish it from the meta
   lines above. */
.tooltipHint {
  margin-top: 0.45rem;
  padding-top: 0.4rem;
  border-top: 1px solid color-mix(in srgb, var(--primary) 30%, transparent);
  font-family: var(--font-sans);
  font-size: 0.7rem;
  font-style: italic;
  color: var(--text-muted);
}
